import { useEffect, useMemo, useState } from 'react';
import { dedupePeople } from './people.js';
import { collection, doc, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';

export function useDoc(path) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  useEffect(() => {
    if (!path) return undefined;
    return onSnapshot(
      doc(db, path),
      (snap) =>
        setState({
          data: snap.exists() ? { id: snap.id, ...snap.data() } : null,
          loading: false,
          error: null,
        }),
      (error) => setState({ data: null, loading: false, error })
    );
  }, [path]);
  return state;
}

export function useCollection(path, constraints = []) {
  const [state, setState] = useState({ data: [], loading: true, error: null });
  const key = `${path}|${JSON.stringify(constraints)}`;
  useEffect(() => {
    if (!path) return undefined;
    const clauses = constraints.map(([kind, ...args]) =>
      kind === 'where' ? where(...args) : orderBy(...args)
    );
    return onSnapshot(
      query(collection(db, path), ...clauses),
      (snap) =>
        setState({
          data: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
          loading: false,
          error: null,
        }),
      (error) => setState({ data: [], loading: false, error })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state;
}

/** Roster, practice groups, areas of law and field rules — everything the form needs. */
export function useFormConfig() {
  const roster = useCollection('roster', [['where', 'isActive', '==', true]]);
  const groups = useCollection('practiceGroups');
  const areasOfLaw = useDoc('settings/areasOfLaw');
  const fieldRules = useDoc('settings/fieldRules');
  const fieldLayout = useDoc('settings/fieldLayout');
  const fieldDerivations = useDoc('settings/fieldDerivations');
  const general = useDoc('settings/general');
  const feeds = useDoc('settings/feeds');
  // One entry per human: the feed and a first sign-in can each create a roster document
  // for the same person, which showed up as a duplicate in every chooser (lib/people.js).
  // Deliberately NOT applied to Admin -> Roster, which must still show both so the stale
  // one can be deleted.
  const dedupedRoster = useMemo(() => dedupePeople(roster.data), [roster.data]);
  return {
    roster: dedupedRoster,
    practiceGroups: groups.data,
    areasOfLaw: areasOfLaw.data,
    fieldRules: fieldRules.data,
    fieldLayout: fieldLayout.data,
    fieldDerivations: fieldDerivations.data,
    general: general.data,
    feeds: feeds.data,
    loading:
      roster.loading ||
      groups.loading ||
      areasOfLaw.loading ||
      fieldRules.loading ||
      fieldLayout.loading ||
      fieldDerivations.loading ||
      general.loading ||
      feeds.loading,
    error:
      roster.error ||
      groups.error ||
      areasOfLaw.error ||
      fieldRules.error ||
      fieldLayout.error ||
      fieldDerivations.error ||
      general.error ||
      feeds.error,
  };
}
